import React, { createContext, useContext } from 'react'
import { useCandidate } from './CandidateProvider'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { cn } from '~/lib/utils'
import moment from 'moment'
import { useServiceRequests } from './ServicesSection'
import {
  CompletedOnStatus,
  CompletedStatus,
  CalloutStatus,
  PendingStatus,
} from './ResumeReviewDetail'
import RevisionDialog from './RevisionDialog'
import OrientationFeedbackDialog from './OrientationFeedbackDialog'
import ViewCoachFeedbackDialog from './ViewCoachFeedbackDialog'

const OrientationCallContext = createContext()
export const useOrientationCall = () => useContext(OrientationCallContext) || {}

const OrientationCallDetail = () => {
  const { detailItemId } = useCandidate()
  const { serviceRequests } = useServiceRequests()
  const currentOrientationCall = serviceRequests?.find(
    (serviceRequest) => serviceRequest.id == detailItemId,
  )

  return (
    <OrientationCallContext.Provider value={currentOrientationCall}>
      <div className="flex items-center gap-4">
        <h2 className="font-roboto-slab text-intoo-blue-medium">
          <a href="#">Coaching Services &gt;</a>
          <span className="ml-2 underline">Orientation Call Progress</span>
        </h2>
        <div className="text-lg">
          <StatusIndicator />
        </div>
      </div>
      <div className="my-4 text-lg">
        Premium Introductory Calls are 50 minute meetings to allow the candidate
        to learn about their coach, the services that are available, and to
        share their goals for the program. This service is only provided once
        for each candidate.
      </div>
      <DetailTable />
    </OrientationCallContext.Provider>
  )
}

export default OrientationCallDetail

const DetailTable = () => {
  return (
    <Table
      className="w-full border-separate"
      tableClass="rounded-2xl border border-silver"
    >
      <OrientationCallHeading />
      <TableBody>
        <CreatedAtRow />
        <AssignmentRow />
        <MeetingRow />
        <MeetingFeedbackRow />
      </TableBody>
    </Table>
  )
}

const OrientationCallHeading = () => {
  const cells = [
    'Service Progress',
    'Status',
    'Due Date',
    'Documents',
    'Actions',
    'Invoiced Time (minutes)',
  ]
  return (
    <TableHeader className="bg-intoo-blue-medium-foo h-6">
      <TableRow className="bg-intoo-blue-medium text-lg hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}

const CreatedAtRow = () => {
  const { createdAt, message } = useOrientationCall()

  return (
    <StripedRow>
      <TableCell className="text-medium">Orientation Call Requested</TableCell>
      <TableCell>
        <CompletedOnStatus completedAt={createdAt} />
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      {message ? (
        <TableCell>
          <div className="flex flex-col gap-2">
            <RevisionDialog type="Candidate">View Request Note</RevisionDialog>
          </div>
        </TableCell>
      ) : (
        <TableCell>&mdash;</TableCell>
      )}
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const AssignmentRow = () => {
  const { assignedAt, assignmentDueDate, coachId, currentUserIsCoach } =
    useOrientationCall()
  const displayAssignmentDue = assignmentDueDate
    ? moment(assignmentDueDate).format('MM/DD/YYYY')
    : null
  return (
    <StripedRow>
      <TableCell className="text-medium">Coach Assigned</TableCell>
      <TableCell>
        {assignedAt ? (
          <CompletedOnStatus completedAt={assignedAt} />
        ) : currentUserIsCoach ? (
          <CompletedStatus>Assigned to You</CompletedStatus>
        ) : coachId ? (
          <PendingStatus>Assigned to Another Coach</PendingStatus>
        ) : (
          <PendingStatus>Incomplete</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {assignedAt ? <span>&mdash;</span> : displayAssignmentDue}
      </TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>{assignedAt ? 5 : <span>&mdash;</span>}</TableCell>
    </StripedRow>
  )
}

const MeetingRow = () => {
  const { completedEvents, pendingEvents, meetingViaCalendly } =
    useOrientationCall()
  const currentCompletedEvent = completedEvents?.[0]
  const currentPendingEvent = pendingEvents?.[0]
  const displayCompletedTime = currentCompletedEvent
    ? moment(currentCompletedEvent?.endTime).format('MM/DD/YYYY')
    : null
  const scheduledForTime = currentPendingEvent
    ? moment(pendingEvents?.[0]?.startTime).format('dddd MMMM Do HH:mm A')
    : null
  const scheduledForDate = currentPendingEvent
    ? moment(pendingEvents?.[0]?.startTime).format('MM/DD/YYYY')
    : null
  const { joinUrl } = currentPendingEvent || {}

  if (!meetingViaCalendly) {
    return (
      <StripedRow>
        <TableCell className="text-medium">Meeting Info</TableCell>
        <TableCell>
          <PendingStatus>N/A</PendingStatus>
        </TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
        <TableCell>&mdash;</TableCell>
      </StripedRow>
    )
  }

  return (
    <StripedRow>
      <TableCell className="text-medium">Meeting Info</TableCell>
      <TableCell>
        {completedEvents?.length > 0 ? (
          <CompletedOnStatus completedAt={displayCompletedTime} />
        ) : pendingEvents?.length > 0 ? (
          <CompletedStatus>Scheduled for {scheduledForTime}</CompletedStatus>
        ) : (
          <PendingStatus>Awaiting Candidate</PendingStatus>
        )}
      </TableCell>
      <TableCell>{scheduledForDate || <span>&mdash;</span>}</TableCell>
      <TableCell>&mdash;</TableCell>
      <TableCell>
        {currentPendingEvent ? (
          <a href={joinUrl} target="_blank" rel="noopener noreferrer">
            Join Meeting
          </a>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>&mdash;</TableCell>
    </StripedRow>
  )
}

const MeetingFeedbackRow = () => {
  const { dueAt, status, feedbackDuration, completedAt } = useOrientationCall()
  const displayDueAt = dueAt ? moment(dueAt).format('MM/DD/YYYY') : null
  const isActionable = status == 'scheduled'
  const isCompleted = status == 'closed'
  console.log('completedAt', completedAt)
  return (
    <StripedRow>
      <TableCell className="text-medium">Meeting Feedback</TableCell>
      <TableCell>
        {isActionable ? (
          <CalloutStatus>Awaiting Coach Feedback</CalloutStatus>
        ) : isCompleted ? (
          <CompletedOnStatus completedAt={completedAt} />
        ) : (
          <PendingStatus>Not Started</PendingStatus>
        )}
      </TableCell>
      <TableCell>
        {(isActionable && displayDueAt) || <span>&mdash;</span>}
      </TableCell>
      <TableCell>
        {isCompleted ? (
          <ViewCoachFeedbackDialog>View Coach Feedback</ViewCoachFeedbackDialog>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        {isActionable ? (
          <OrientationFeedbackDialog>Add Feedback</OrientationFeedbackDialog>
        ) : (
          <span>&mdash;</span>
        )}
      </TableCell>
      <TableCell>
        {feedbackDuration ? feedbackDuration : <span>&mdash;</span>}
      </TableCell>
    </StripedRow>
  )
}

const StripedRow = ({ children, className, ...props }) => {
  return (
    <TableRow
      {...props}
      className={cn(
        'odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}

const StatusIndicator = ({ serviceRequest }) => {
  // If serviceRequest is passed, use its fields; otherwise, use context
  const context = useOrientationCall()
  const { status, displayStatus, completedAt, meetingViaCalendly } =
    serviceRequest || context

  return (
    <div>
      {isActionable(status, meetingViaCalendly) ? (
        <CalloutStatus>{displayStatus}</CalloutStatus>
      ) : completedAt ? (
        <CompletedOnStatus date={completedAt} />
      ) : (
        <PendingStatus>{displayStatus}</PendingStatus>
      )}
    </div>
  )
}

const isActionable = (status, meetingViaCalendly) => {
  return (
    ['revision_requested', 'scheduled'].includes(status) ||
    (!meetingViaCalendly && status === 'assigned')
  )
}
